// @ts-nocheck -- Custom store script. Runtime behaviour is covered by unit tests;
// the theme's strict checkJs config is geared to Shopify's own fully-annotated
// modules and isn't applied to this bespoke component.
import { DialogComponent } from '@theme/dialog';
import '@theme/usage-gauge';

/**
 * Usage Details panel.
 *
 * A `dialog-modal dialog-drawer` slide-out (opened from the header). On first open it
 * loads the logged-in customer's eSIMs from the App Proxy (or bundled mock fixtures),
 * renders a selectable card per eSIM, and — for each selected eSIM — a usage
 * slide with a semicircle gauge. Nickname / selection / hidden state is a
 * per-customer VIEW preference kept in localStorage (removing never cancels a
 * plan). Backend is a follow-up; the mock returns the documented contract shape.
 *
 * Config (attributes):
 *   data-endpoint     - App Proxy base, e.g. "/apps/esimsta"
 *   data-mock         - "true" to use bundled fixtures instead of fetch
 *   data-logged-in    - "true" when a customer session exists (Liquid gate)
 *   data-customer-id  - Shopify customer id, used to scope localStorage
 *
 * @typedef {Object} UsageRefs
 * @property {HTMLElement} [list]
 * @property {HTMLElement} [usage]
 * @property {HTMLElement} [carousel]
 * @property {HTMLElement} [dots]
 * @property {HTMLElement} [cardsDots]
 * @property {HTMLElement} [editButton]
 * @property {HTMLElement} [i18n]
 * @property {HTMLTemplateElement} [cardTemplate]
 * @property {HTMLTemplateElement} [slideTemplate]
 *
 * @typedef {{ iccid: string, country?: string|null, countryCode?: string|null, productHandle?: string|null, productId?: string|number, orderId?: string|number, plan?: string, unlimited?: boolean }} Esim
 * @typedef {{ name?: string|null, selected?: boolean, hidden?: boolean }} EsimPref
 *
 * Extends DialogComponent so the panel uses the theme's `dialog-modal dialog-drawer`
 * slide-out shell (same primitive as the cart/login drawers), inheriting showDialog/
 * closeDialog, focus trap, scroll lock and backdrop handling.
 *
 * @extends {DialogComponent}
 */
export class UsageDetailsComponent extends DialogComponent {
  #loaded = false;
  /** @type {Esim[]} */
  #esims = [];
  /** @type {Record<string, any>} */
  #usageCache = {};
  /** @type {string | null} */
  #pendingRemove = null;

  connectedCallback() {
    super.connectedCallback();
    // Keep the name input focused when its pencil/check button is pressed: mousedown
    // fires before focus moves, so canceling it prevents a premature blur-commit and
    // lets toggleRename own the read-only state.
    this.addEventListener('mousedown', this.#keepRenameFocus);
  }

  #keepRenameFocus = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.usage-card__rename')) event.preventDefault();
  };

  /** Opens the drawer; lazy-loads the customer's eSIMs on first open. Wired from the header trigger via `on:click="/open"`. */
  open() {
    this.showDialog();
    if (this.#loaded || !this.loggedIn) return;
    this.#loaded = true;
    this.#load();
  }

  /** Closes the drawer (header close button). */
  close() {
    this.closeDialog();
  }

  get endpoint() {
    return this.getAttribute('data-endpoint') || '/apps/esimsta';
  }
  get mock() {
    return this.getAttribute('data-mock') === 'true';
  }
  get loggedIn() {
    return this.getAttribute('data-logged-in') === 'true';
  }
  get storageKey() {
    return `roamigo:usage-prefs:${this.getAttribute('data-customer-id') || 'guest'}`;
  }
  /**
   * Typed view over refs. The base DialogComponent narrows `this.refs` to `{ dialog }`,
   * so this widens it to the panel's own refs for the render code below.
   * @returns {UsageRefs}
   */
  get #ui() {
    return /** @type {any} */ (this.refs);
  }

  get strings() {
    if (this.#strings) return this.#strings;
    try {
      this.#strings = JSON.parse(this.#ui.i18n?.textContent || '{}');
    } catch {
      this.#strings = {};
    }
    return this.#strings;
  }
  /** @type {Record<string, string> | null} */
  #strings = null;

  // --- destinations (resolved from the store's collections) -----------------

  /**
   * productId -> destination name. Built from the `destinations` island: regions
   * first, then countries, so a product in both (e.g. tagged `indonesia, asia`)
   * resolves to the country.
   */
  get #destinations() {
    if (this.#destinationMap) return this.#destinationMap;
    const data = this.#destinationData;
    this.#destinationMap = { ...(data.regions || {}), ...(data.countries || {}) };
    return this.#destinationMap;
  }
  /** @type {Record<string, string> | null} */
  #destinationMap = null;

  /** The parsed `destinations` island: product maps, name -> flag URL, and the Files CDN prefix. */
  get #destinationData() {
    if (this.#destinationIsland) return this.#destinationIsland;
    let data = {};
    try {
      data = JSON.parse(this.#ui.destinations?.textContent || '{}') || {};
    } catch {
      data = {};
    }
    this.#destinationIsland = data;
    return data;
  }
  /** @type {Record<string, any> | null} */
  #destinationIsland = null;

  /**
   * Destination label for a card, in this order:
   *
   *   1. `country` from the middleware — stored per eSIM at provisioning time
   *      (since 2026-09-04), so it is the record of what was actually sold and
   *      wins outright. It is spelled like the collection title ("Japan",
   *      "Asia, Oceania"), which is what the flag lookup keys on. Null when the
   *      middleware could not resolve it; never a placeholder.
   *   2. `productHandle` against the store's collections. Handles survive
   *      re-imports; ids do not.
   *   3. `productId` against the same collections.
   *   4. `orderId` against the customer's own Shopify orders, whose line item
   *      remembers the product (and its title) even after the product is gone.
   *
   * Empty string when nothing resolves; the card then shows a neutral disc.
   */
  #destination(esim) {
    const served = typeof esim.country === 'string' ? esim.country.trim() : '';
    if (served) return served;

    const handle = typeof esim.productHandle === 'string' ? esim.productHandle.trim() : '';
    const byHandle = handle ? this.#handles[handle] : '';
    if (byHandle) return byHandle;

    const id = esim.productId == null ? '' : String(esim.productId);
    const order = this.#orderEntry(esim);
    return this.#destinations[id] || this.#destinations[order.productId] || order.name || '';
  }

  /** productHandle -> destination name, merged country-last like the id map. */
  get #handles() {
    if (this.#handleMap) return this.#handleMap;
    const data = this.#destinationData;
    this.#handleMap = { ...(data.region_handles || {}), ...(data.country_handles || {}) };
    return this.#handleMap;
  }
  /** @type {Record<string, string> | null} */
  #handleMap = null;

  /**
   * The customer's own Shopify order behind an eSIM, from the `orders` island
   * (keyed by id, "#name" and bare number): `{ productId, name }`, where `name`
   * is the destination the theme worked out from the order line itself. Empty
   * object when the eSIM has no orderId or the order is not this customer's.
   */
  #orderEntry(esim) {
    const orderId = esim.orderId ?? esim.order_id ?? esim.orderNumber ?? esim.order_number;
    if (orderId == null || orderId === '') return {};
    const orders = this.#destinationData.orders || {};
    const key = String(orderId);
    const hit = orders[key] ?? orders[key.replace(/^#/, '')] ?? orders[`#${key.replace(/^#/, '')}`];
    if (hit == null) return {};
    // Tolerate the earlier shape, where the value was the bare productId.
    if (typeof hit !== 'object') return { productId: String(hit), name: '' };
    return { productId: hit.productId == null ? '' : String(hit.productId), name: hit.name || '' };
  }

  /**
   * Flag image candidates for a destination name, most specific first: the
   * destination collection's image, then the Settings > Files spellings the
   * coverage popup also tries (`Flag_of_Japan.webp`, `Flag_of_the_Netherlands.webp`,
   * and both again as .png). Stacked as CSS backgrounds, so a URL that 404s
   * paints nothing and the next candidate shows through.
   */
  #flagCandidates(name) {
    if (!name) return [];
    const data = this.#destinationData;
    const urls = [];
    const fromCollection = data.flags && data.flags[name];
    if (fromCollection) urls.push(fromCollection);
    const base = typeof data.files_base === 'string' ? data.files_base : '';
    if (base) {
      const stem = name.trim().replace(/\s+/g, '_');
      for (const prefix of ['Flag_of_', 'Flag_of_the_']) {
        for (const ext of ['.webp', '.png']) urls.push(`${base}${prefix}${stem}${ext}`);
      }
    }
    return urls;
  }

  // --- preferences (customer metafield, cached in localStorage) -------------

  /**
   * Merges the server-side display settings (the roamigo.esim_display_settings
   * customer metafield, rendered into the `prefs` island) into the local cache.
   * The metafield is the source of truth for nickname/hidden across devices;
   * localStorage still carries device-local state like `selected`.
   */
  #syncServerPrefs() {
    let server = {};
    try {
      server = JSON.parse(this.#ui.prefs?.textContent || '{}') || {};
    } catch {
      return;
    }
    if (!server || typeof server !== 'object') return;

    const prefs = this.#prefs();
    for (const [iccid, entry] of Object.entries(server)) {
      if (!entry || typeof entry !== 'object') continue;
      prefs[iccid] = {
        ...prefs[iccid],
        name: entry.nickname ?? null,
        hidden: Boolean(entry.hidden),
      };
    }
    this.#savePrefs(prefs);
  }

  /**
   * Persists a display setting to the customer metafield. Fire-and-forget: the UI
   * has already updated optimistically from localStorage, so a failed write only
   * means the change doesn't follow the customer to another device.
   */
  async #persistPref(iccid, patch) {
    if (this.mock) return;
    try {
      await fetch(`${this.endpoint}/esim/nickname`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ iccid, ...patch }),
      });
    } catch (error) {
      console.warn('[usage-details] could not save display setting', error);
    }
  }

  #prefs() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '{}');
    } catch {
      return {};
    }
  }
  #savePrefs(prefs) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(prefs));
    } catch {
      /* private mode / quota — selection just won't persist */
    }
  }
  #pref(iccid) {
    return this.#prefs()[iccid] || {};
  }
  #setPref(iccid, patch) {
    const prefs = this.#prefs();
    prefs[iccid] = { ...prefs[iccid], ...patch };
    this.#savePrefs(prefs);
  }

  // --- loading --------------------------------------------------------------

  async #load() {
    this.#setState('loading');
    // Server-side display settings win over the local cache before the first render.
    this.#syncServerPrefs();
    try {
      const data = await this.#fetch('/esim/list');
      if (!data || data.loggedIn === false) {
        // Session expired between page load and open — show the signed-out slot.
        this.setAttribute('data-logged-in', 'false');
        this.#setState('signedout');
        return;
      }
      this.#esims = Array.isArray(data.esims) ? data.esims : [];
      this.#renderCards();
      this.#renderHiddenNote();
      await this.#renderUsage();
      this.#setState(this.#visibleEsims().length ? 'ready' : 'empty');
    } catch (error) {
      console.error('[usage-details] load failed', error);
      this.#setState('error');
    }
  }

  async #fetch(path) {
    if (this.mock) return mockResponse(path);
    const res = await fetch(this.endpoint + path, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  #setState(state) {
    this.setAttribute('data-state', state);
  }

  /**
   * Cards to render. Hidden eSIMs are filtered out normally, but reappear
   * (dimmed, with a restore button) in edit mode — otherwise hiding would be a
   * one-way door with no way back.
   */
  #visibleEsims() {
    if (this.hasAttribute('editing')) return this.#esims;
    return this.#esims.filter((e) => !this.#pref(e.iccid).hidden);
  }

  #hiddenCount() {
    return this.#esims.filter((e) => this.#pref(e.iccid).hidden).length;
  }

  /** Surfaces that hidden eSIMs exist, so restoring them is discoverable. */
  #renderHiddenNote() {
    const note = this.#ui.hiddenNote;
    if (!note) return;
    const count = this.#hiddenCount();
    const show = count > 0 && !this.hasAttribute('editing');
    note.textContent = show ? interpolate(this.strings.hidden_count, { count }) : '';
    note.toggleAttribute('hidden', !show);
  }

  /** Re-renders the list and syncs the empty/ready state after a visibility change. */
  #refreshList() {
    this.#renderCards();
    this.#renderHiddenNote();
    this.#setState(this.#visibleEsims().length ? 'ready' : 'empty');
  }

  // --- card rendering -------------------------------------------------------

  #renderCards() {
    const list = this.#ui.list;
    const template = this.#ui.cardTemplate;
    if (!list || !(template instanceof HTMLTemplateElement)) return;
    const first = template.content.firstElementChild;
    if (!first) return;

    const esims = this.#visibleEsims();
    list.textContent = '';
    if (this.#ui.cardsDots) this.#ui.cardsDots.textContent = '';

    // 2 or fewer eSIMs: a plain vertical list.
    if (esims.length <= 2) {
      list.classList.remove('usage-cards--slider');
      for (const esim of esims) list.appendChild(this.#buildCard(first, esim));
      return;
    }

    // More than two: a horizontal slider, two cards per page. Every page stretches to
    // the tallest page's height (two rows), so the drawer keeps a constant height when
    // you swipe to a page that only has one card.
    list.classList.add('usage-cards--slider');
    const pages = [];
    for (let i = 0; i < esims.length; i += 2) {
      const page = document.createElement('div');
      page.className = 'usage-cards__page';
      for (const esim of esims.slice(i, i + 2)) page.appendChild(this.#buildCard(first, esim));
      list.appendChild(page);
      pages.push(page);
    }
    this.#buildCardDots(list, pages);
  }

  #buildCard(first, esim) {
    const pref = this.#pref(esim.iccid);
    const node = /** @type {HTMLElement} */ (first.cloneNode(true));
    node.dataset.iccid = esim.iccid;

    const name = pref.name || this.#defaultName(esim);
    const destination = this.#destination(esim);
    setField(node, 'iccid', esim.iccid);
    setField(node, 'country', destination);
    setField(node, 'plan', esim.plan || '');

    // The disc stays even with no destination (neutral grey, no name) so the card keeps
    // its two-line shape and the gap is visible as a gap rather than a missing row.
    const flag = /** @type {HTMLElement | null} */ (node.querySelector('[data-field="flag"]'));
    if (flag) {
      const candidates = this.#flagCandidates(destination);
      if (candidates.length) flag.style.backgroundImage = candidates.map((url) => `url("${url}")`).join(', ');
    }

    const nameInput = /** @type {HTMLInputElement | null} */ (node.querySelector('[data-field="name"]'));
    if (nameInput) {
      nameInput.value = name;
      nameInput.setAttribute('aria-label', this.strings.rename || 'Rename');
    }
    const select = /** @type {HTMLInputElement | null} */ (node.querySelector('input[type="checkbox"]'));
    if (select) {
      select.checked = !!pref.selected;
      select.setAttribute('aria-label', interpolate(this.strings.select_esim, { name }));
    }
    const remove = node.querySelector('.usage-card__remove');
    remove?.setAttribute('aria-label', interpolate(this.strings.remove_esim, { name }));
    const restore = node.querySelector('.usage-card__restore');
    restore?.setAttribute('aria-label', interpolate(this.strings.restore_esim, { name }));

    node.classList.toggle('is-selected', !!pref.selected);
    node.classList.toggle('usage-card--hidden', !!pref.hidden);
    return node;
  }

  #buildCardDots(track, pages) {
    const dots = this.#ui.cardsDots;
    if (!dots || pages.length < 2) return;

    pages.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'usage-dot';
      dot.setAttribute('on:click', `/goToCardPage/${index}`);
      dot.setAttribute('aria-label', `${index + 1}`);
      if (index === 0) dot.setAttribute('aria-current', 'true');
      dots.appendChild(dot);
    });

    this.#cardObserver?.disconnect();
    this.#cardObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = pages.indexOf(entry.target);
          Array.from(dots.children).forEach((dot, i) => setActiveDot(dot, i === index));
        }
      },
      { root: track, threshold: 0.6 }
    );
    pages.forEach((page) => this.#cardObserver.observe(page));
  }
  #cardObserver = null;

  goToCardPage(index) {
    const page = this.#ui.list?.children[index];
    page?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }

  #defaultName(esim) {
    const index = this.#esims.indexOf(esim);
    return interpolate(this.strings.default_name, { number: index + 1 });
  }

  // --- interactions (wired via on: attributes in the template) --------------

  /** @param {Event} event */
  toggleSelect(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const card = input.closest('.usage-card');
    if (!(card instanceof HTMLElement) || !card.dataset.iccid) return;
    const selected = input.checked;
    this.#setPref(card.dataset.iccid, { selected });
    card.classList.toggle('is-selected', selected);
    this.#renderUsage();
  }

  /**
   * Pencil button: toggles the name field between read-only and editable. While
   * editable the pencil renders as a check (CSS, via the input's :read-write state);
   * clicking it commits the rename. A mousedown guard (see connectedCallback) stops
   * the button press from blurring the input first, so this toggle owns the state.
   * @param {Event} event
   */
  toggleRename(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const input = target.closest('.usage-card')?.querySelector('[data-field="name"]');
    if (!(input instanceof HTMLInputElement)) return;
    if (input.readOnly) {
      input.readOnly = false;
      input.focus();
      input.select();
    } else {
      this.#commitName(input);
    }
  }

  /** @param {Event} event — fires when the field loses focus by clicking elsewhere. */
  commitRename(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input instanceof HTMLInputElement) this.#commitName(input);
  }

  #commitName(input) {
    const card = input.closest('.usage-card');
    if (!(card instanceof HTMLElement) || !card.dataset.iccid) return;
    const iccid = card.dataset.iccid;
    input.readOnly = true;
    const value = input.value.trim();
    const esim = this.#esims.find((e) => e.iccid === iccid);
    input.value = value || (esim ? this.#defaultName(esim) : '');
    this.#setPref(iccid, { name: value || null });
    this.#persistPref(iccid, { nickname: value });
    // Keep the usage slide's title in sync if this eSIM is currently shown.
    if (this.#pref(iccid).selected) this.#renderUsage();
  }

  toggleEdit() {
    const editing = this.toggleAttribute('editing');
    const button = this.#ui.editButton;
    if (button) button.textContent = (editing ? this.strings.done : this.strings.edit) || '';
    // Entering/leaving edit mode changes whether hidden eSIMs are listed.
    this.#refreshList();
  }

  /** Un-hides an eSIM from edit mode (the inverse of confirmRemove). */
  restoreEsim(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const card = target.closest('.usage-card');
    if (!(card instanceof HTMLElement) || !card.dataset.iccid) return;
    const iccid = card.dataset.iccid;
    this.#setPref(iccid, { hidden: false });
    this.#persistPref(iccid, { hidden: false });

    // Update the card in place rather than re-rendering the list (same approach
    // as toggleSelect). Re-rendering here would detach the clicked button
    // mid-dispatch, and DialogComponent's outside-click check — which is
    // `!dialog.contains(event.target)` — would then read the detached button as
    // a click outside the drawer and close it.
    // Safe to skip a re-render: edit mode lists every eSIM regardless of
    // `hidden`, so restoring changes styling only, not list membership. Leaving
    // edit mode re-renders via toggleEdit().
    card.classList.remove('usage-card--hidden');
    this.#renderHiddenNote();
    this.#setState(this.#visibleEsims().length ? 'ready' : 'empty');
  }

  /** @param {Event} event */
  requestRemove(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const card = target.closest('.usage-card');
    if (!(card instanceof HTMLElement)) return;
    this.#pendingRemove = card.dataset.iccid || null;
    /** @type {any} */ (document.getElementById('usage-confirm-remove'))?.showDialog?.();
  }

  confirmRemove() {
    const iccid = this.#pendingRemove;
    this.#pendingRemove = null;
    /** @type {any} */ (document.getElementById('usage-confirm-remove'))?.closeDialog?.();
    if (!iccid) return;
    this.#setPref(iccid, { hidden: true, selected: false });
    this.#persistPref(iccid, { hidden: true });
    this.#refreshList();
    this.#renderUsage();
  }

  // --- usage slides + carousel ----------------------------------------------

  async #renderUsage() {
    const region = this.#ui.usage;
    const track = this.#ui.carousel;
    const template = this.#ui.slideTemplate;
    if (!region || !track) return;

    const selected = this.#visibleEsims().filter((e) => this.#pref(e.iccid).selected);
    track.textContent = '';
    if (this.#ui.dots) this.#ui.dots.textContent = '';

    // Only reveal the section once at least one slide is actually in the DOM — a fetch
    // or build failure must not leave an empty "Active Plan Usage" header hanging.
    let count = 0;
    if (template instanceof HTMLTemplateElement) {
      for (const esim of selected) {
        let usage = this.#usageCache[esim.iccid];
        if (!usage) {
          try {
            usage = await this.#fetch(`/esim/usage?iccid=${encodeURIComponent(esim.iccid)}`);
            this.#usageCache[esim.iccid] = usage;
          } catch (error) {
            console.error('[usage-details] usage failed', esim.iccid, error);
            continue;
          }
        }
        try {
          const slide = this.#buildSlide(esim, usage);
          if (slide) {
            track.appendChild(slide);
            count += 1;
          }
        } catch (error) {
          console.error('[usage-details] slide build failed', esim.iccid, error);
        }
      }
    }

    region.toggleAttribute('hidden', count === 0);
    if (count > 1) this.#buildDots(track);
  }

  #buildSlide(esim, usage) {
    const first = this.#ui.slideTemplate?.content?.firstElementChild;
    if (!first) return null;
    const node = first.cloneNode(true);
    const name = this.#pref(esim.iccid).name || this.#defaultName(esim);
    const unlimited = !!usage.unlimited;
    const allowanceMb = Number(usage.allowanceMb) || 0;
    const usedMb = Number(usage.usedMb) || 0;
    const daysLeft = usage.daysLeft != null ? usage.daysLeft : daysUntil(usage.expiresAt);

    setField(node, 'name', name);
    setField(node, 'days', interpolate(this.strings.days_left, { days: daysLeft ?? '—' }));
    setField(node, 'allowance', unlimited ? this.strings.unlimited : fmtGB(allowanceMb));
    setField(node, 'used', fmtGB(usedMb));

    const gauge = node.querySelector('usage-gauge');
    if (gauge) {
      gauge.setAttribute('used', String(usedMb));
      gauge.setAttribute('allowance', String(allowanceMb));
      gauge.setAttribute('unlimited', String(unlimited));
      gauge.setAttribute('label', `${name}: ${fmtGB(usedMb)} / ${unlimited ? this.strings.unlimited : fmtGB(allowanceMb)}`);
    }
    return node;
  }

  #buildDots(track) {
    const dots = this.#ui.dots;
    const slides = Array.from(track.children);
    if (!dots || slides.length < 2) return;

    slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'usage-dot';
      dot.setAttribute('on:click', `/goToSlide/${index}`);
      dot.setAttribute('aria-label', `${index + 1}`);
      if (index === 0) dot.setAttribute('aria-current', 'true');
      dots.appendChild(dot);
    });

    // Sync the active dot to whichever slide is scrolled into view.
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = slides.indexOf(entry.target);
          Array.from(dots.children).forEach((dot, i) => setActiveDot(dot, i === index));
        }
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((slide) => this.#observer.observe(slide));
  }
  #observer = null;

  goToSlide(index) {
    const slide = this.#ui.carousel?.children[index];
    slide?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }
}

// --- helpers ----------------------------------------------------------------

/** Sets aria-current="true" (matches the CSS) or removes it. toggleAttribute would
 *  write aria-current="" which the [aria-current='true'] selector doesn't match. */
function setActiveDot(dot, active) {
  if (active) dot.setAttribute('aria-current', 'true');
  else dot.removeAttribute('aria-current');
}

function setField(root, field, value) {
  const el = root.querySelector(`[data-field="${field}"]`);
  if (el) el.textContent = value;
}

/**
 * Fills %token% placeholders. The Liquid island renders each interpolated string
 * with a %name% sentinel (e.g. `| t: days: '%days%'`) rather than passing the real
 * value, so the string is localized once server-side and finished here. Sentinels
 * avoid nested {{ }} in Liquid and don't depend on missing-variable passthrough.
 */
function interpolate(template, vars) {
  if (!template) return '';
  return template.replace(/%(\w+)%/g, (match, key) => (key in vars ? String(vars[key]) : match));
}

/** MB -> "3GB" / "2.75GB" (up to 2 decimals, trailing zeros trimmed). */
function fmtGB(mb) {
  const gb = mb / 1024;
  const rounded = Math.round(gb * 100) / 100;
  return `${rounded}GB`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (isNaN(then)) return null;
  return Math.max(0, Math.ceil((then - Date.now()) / 86400000));
}

// --- mock fixtures (documented App Proxy contract) --------------------------

const MOCK = {
  list: {
    loggedIn: true,
    esims: [
      {
        id: 'es_1',
        iccid: '89852123456789012345',
        country: 'Japan',
        countryCode: 'JP',
        plan: '3 Days · 3GB/Day + Unlimited',
        unlimited: true,
        status: 'active',
        orderId: 1001,
      },
      {
        id: 'es_2',
        iccid: '89852123456789099999',
        country: 'Japan',
        countryCode: 'JP',
        plan: '3 Days · 3GB',
        unlimited: false,
        status: 'active',
        orderId: 1002,
      },
      {
        id: 'es_3',
        iccid: '89852152175812857218',
        country: 'Indonesia',
        countryCode: 'ID',
        plan: '3 Days · 3GB',
        unlimited: false,
        status: 'active',
        orderId: 1003,
      },
    ],
  },
  usage: {
    '89852123456789012345': {
      iccid: '89852123456789012345',
      unlimited: true,
      allowanceMb: 3072,
      usedMb: 1536,
      remainingMb: 1536,
      daysLeft: 2,
      status: 'active',
    },
    '89852123456789099999': {
      iccid: '89852123456789099999',
      unlimited: false,
      allowanceMb: 3072,
      usedMb: 2816,
      remainingMb: 256,
      daysLeft: 1,
      status: 'active',
    },
    '89852152175812857218': {
      iccid: '89852152175812857218',
      unlimited: false,
      allowanceMb: 3072,
      usedMb: 2816,
      remainingMb: 256,
      daysLeft: 1,
      status: 'active',
    },
  },
};

function mockResponse(path) {
  if (path.startsWith('/esim/list')) return structuredClone(MOCK.list);
  if (path.startsWith('/esim/usage')) {
    const iccid = new URLSearchParams(path.split('?')[1] || '').get('iccid');
    return structuredClone(MOCK.usage[iccid] || { iccid, unlimited: false, allowanceMb: 0, usedMb: 0, daysLeft: null });
  }
  return null;
}

if (!customElements.get('usage-details-component')) {
  customElements.define('usage-details-component', UsageDetailsComponent);
}
